// src/lib/kb/client.ts
import { supabase } from "@/lib/supabase";
import type { Audience, KbAnswerResponse, KbLane } from "./types";

export type KbAnswerRequest = {
  query: string;
  audience?: Audience;
  locationId?: string | null;
  organizationId?: string | null;
  scopes?: string[] | null;
  topK?: number;
  minConfidence?: number; // default server-side is 0.72
  minSimilarity?: number; // deprecated alias (use minConfidence)
  guidancePurpose?: "contacts" | "policy" | "advice" | "pricing";
  guidanceProduct?: "all" | "sheds" | "steel";
  guidanceSource?: "user_selected" | "auto_rules" | "default";
  lane?: KbLane;
  laneOverride?: KbLane | null;
};

export function confidenceFromConfidence(bestConfidence: number): number {
  return Math.round(Math.max(0, Math.min(1, bestConfidence || 0)) * 100);
}

// Back-compat (older callers)
export function confidenceFromSimilarity(bestSimilarity: number): number {
  return confidenceFromConfidence(bestSimilarity);
}

export async function kbAnswer(req: KbAnswerRequest): Promise<KbAnswerResponse> {
  const token = supabase ? (await supabase.auth.getSession()).data.session?.access_token : undefined;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const resp = await fetch("/api/kb/answer", {
    method: "POST",
    headers,
    body: JSON.stringify(req),
  });

  // Note: /api/kb/answer returns 500 with { error, details } on server error.
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`kbAnswer failed (${resp.status}): ${text.slice(0, 800)}`);
  }

  const json = (await resp.json()) as KbAnswerResponse;

  // Client-side contract guard (keeps UI stable if API drifts)
  if (!json || (json.mode !== "answer" && json.mode !== "no_answer" && json.mode !== "suggested_answer")) {
    throw new Error("kbAnswer: unexpected response shape");
  }

  return json;
}
